import React from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'
import { Calendar, Pill, Truck, RefreshCw } from 'lucide-react'

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2', error:'#C0392B',
}

const inp = (focused) => ({
  width: '100%', padding: '.875rem 1.1rem',
  border: `1.5px solid ${focused ? C.g500 : C.border}`,
  borderRadius: 10, fontSize: '.9rem', outline: 'none',
  fontFamily: "'DM Sans',sans-serif", color: C.text, background: 'white',
  boxSizing: 'border-box', transition: 'border-color .2s',
})

function WhatsAppIcon({ color = 'white', size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  )
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuth()

  const redirect = new URLSearchParams(location.search).get('redirect') || null
  const fromCheckout = redirect === '/checkout'

  // Only the role is kept: the chosen role decides which OTP path is used.
  const [form, setForm] = React.useState({ role: 'patient' })
  // Everyone signs in with a WhatsApp code - patients on their own number,
  // doctors on the number held against their approved record. The password
  // endpoint still exists for use from a browser if WhatsApp ever stops
  // delivering, but it is not offered here: one way in is one way to get wrong.
  const [focused, setFocused] = React.useState(null)
  const [error, setError] = React.useState('')

  // WhatsApp OTP state (patients always use this)
  const [phone, setPhone] = React.useState('')
  const [otp, setOtp] = React.useState('')
  const [otpSent, setOtpSent] = React.useState(false)
  const [otpLoading, setOtpLoading] = React.useState(false)
  const [devOtp, setDevOtp] = React.useState('')         // shown when WhatsApp OTP is not configured
  const [resendTimer, setResendTimer] = React.useState(0)

  // Countdown timer for resend
  React.useEffect(() => {
    if (resendTimer <= 0) return
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000)
    return () => clearTimeout(t)
  }, [resendTimer])

  const sendOtp = async () => {
    if (!phone.trim()) { setError('Enter your WhatsApp number.'); return }
    setError(''); setOtpLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), role: form.role }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP')
      setOtpSent(true)
      setResendTimer(30)
      if (data.dev && data.otp) setDevOtp(data.otp)   // dev mode: show OTP on screen
    } catch (err) {
      setError(err.message)
    } finally {
      setOtpLoading(false)
    }
  }

  const verifyOtp = async () => {
    if (!otp.trim()) { setError('Enter the OTP.'); return }
    setError(''); setOtpLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), otp: otp.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Invalid OTP')
      if (!data.token) throw new Error('No token returned')
      localStorage.setItem('token', data.token)
      const role = data.user?.role || 'patient'
      auth.login(role, data.user?.name || phone, data.user?.email || phone, data.token, data.refreshToken)
      const home = role === 'doctor' ? '/doctor-dashboard' : '/patient-dashboard'
      setTimeout(() => navigate(redirect || home, { replace: true }), 100)
    } catch (err) {
      setError(err.message)
    } finally {
      setOtpLoading(false)
    }
  }

  // If already logged in, redirect immediately
  React.useEffect(() => {
    if (auth?.user) {
      navigate(redirect || (auth.user.role === 'doctor' ? '/doctor-dashboard' : auth.user.role === 'admin' ? '/admin/dashboard' : '/patient-dashboard'), { replace: true })
    }
  }, [auth?.user, navigate, redirect])

  // (the password sign-in handler lived here; the OTP flow above replaces it)

  return (
    <div className="login-root" style={{ minHeight: '100vh', display: 'flex', background: 'white' }}>

      {/* ── LEFT PANEL (marketing — hidden on mobile) ── */}
      <div className="login-left" style={{ flex: '0 0 45%', background: `linear-gradient(160deg, ${C.g900}, ${C.g800}, ${C.g700})`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '3rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle, rgba(255,255,255,.025) 1px, transparent 1px)`, backgroundSize: '28px 28px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-10%', right: '-10%', width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, rgba(93,174,115,.15) 0%, transparent 65%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '20%', right: '-5%', width: 300, height: 300, borderRadius: '50%', border: '1px solid rgba(255,255,255,.06)', pointerEvents: 'none' }} />

        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', position: 'relative', zIndex: 1 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" fill="none" stroke="white" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
          </div>
          <div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.3rem', color: 'white' }}>Revive</div>
            <div style={{ fontSize: '.6rem', color: 'rgba(255,255,255,.5)', letterSpacing: '.14em', textTransform: 'uppercase' }}>Skin & Hair Experts</div>
          </div>
        </Link>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 'clamp(1.8rem,3vw,2.75rem)', color: 'white', lineHeight: 1.15, marginBottom: '1.25rem' }}>
            Expert skin & hair<br />care at your fingertips
          </h2>
          <p style={{ fontSize: '.9rem', color: 'rgba(255,255,255,.6)', lineHeight: 1.8, marginBottom: '2.5rem', maxWidth: 340 }}>
            Access personalised treatments, track your progress, and manage appointments — all in one place.
          </p>
          {[
            [Calendar, 'Book consultations online for just ₹500'],
            [Pill, 'Get personalised prescription skincare'],
            [Truck, 'Free delivery on all prescribed products'],
            [RefreshCw, 'Track your treatment progress anytime'],
          ].map(([Ic, text]) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '.85rem' }}>
              <span style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Ic size={17} strokeWidth={1.75} color="#fff" /></span>
              <span style={{ fontSize: '.85rem', color: 'rgba(255,255,255,.7)', lineHeight: 1.5 }}>{text}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '1.5rem', position: 'relative', zIndex: 1 }}>
          {[['MD', 'Certified'], ['200+', 'Brands'], ['Free', 'Skin check']].map(([n, l]) => (
            <div key={l}>
              <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.5rem', color: C.g300 }}>{n}</div>
              <div style={{ fontSize: '.7rem', color: 'rgba(255,255,255,.4)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT PANEL (login form) ── */}
      <div className="login-right" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 2rem', background: C.g50 }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          <div style={{ marginBottom: '2.5rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.g100, border: `1px solid ${C.border}`, borderRadius: 999, padding: '.3rem .9rem', marginBottom: '1.25rem' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.g500, display: 'inline-block' }} />
              <span style={{ fontSize: '.7rem', color: C.g600, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 600 }}>Patient & Doctor Login</span>
            </div>
            <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: '2.25rem', color: C.g900, marginBottom: '.5rem', lineHeight: 1.15 }}>Welcome back</h1>
            <p style={{ fontSize: '.875rem', color: C.muted, lineHeight: 1.7 }}>
              {form.role === 'patient'
                ? 'Enter your WhatsApp number to receive a one-time password.'
                : 'Enter the WhatsApp number on your approved doctor record.'}
            </p>
          </div>

          {/* Role toggle */}
          <div style={{ display: 'flex', background: 'white', border: `1px solid ${C.border}`, borderRadius: 12, padding: 4, marginBottom: '1.75rem' }}>
            {['patient', 'doctor'].map(r => (
              <button key={r} type="button" onClick={() => { setForm(f => ({ ...f, role: r })); setOtpSent(false); setPhone(''); setOtp(''); setError('') }}
                style={{ flex: 1, padding: '.65rem', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif", transition: 'all .2s', background: form.role === r ? C.g700 : 'transparent', color: form.role === r ? 'white' : C.muted, boxShadow: form.role === r ? '0 2px 8px rgba(35,82,51,.25)' : 'none' }}>
                {r === 'patient' ? '👤 Patient' : '🩺 Doctor'}
              </button>
            ))}
          </div>

          {/* Checkout redirect notice */}
          {fromCheckout && !error && (
            <div style={{ background:'#FEF9C3', border:'1px solid #FDE047', borderRadius:10, padding:'.85rem 1rem', marginBottom:'1.25rem', fontSize:'.85rem', color:'#854D0E', display:'flex', alignItems:'center', gap:8 }}>
              🔒 Please login as a <strong style={{marginLeft:3,marginRight:3}}>patient</strong> to complete your purchase.
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '.85rem 1rem', marginBottom: '1.25rem', fontSize: '.85rem', color: C.error, display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚠️ {error}
            </div>
          )}

          {/* ── WhatsApp OTP: the only way in, for patients and doctors alike ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!otpSent ? (
                <>
                  <div>
                    <label style={{ fontSize: '.75rem', fontWeight: 600, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', display: 'block', marginBottom: '.4rem' }}>WhatsApp Number</label>
                    <div style={{ display: 'flex', gap: '.5rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 .75rem', background: C.g50, border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: '.9rem', color: C.muted, flexShrink: 0 }}>+91</span>
                      <input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0,10))}
                        placeholder="9876543210" maxLength={10} autoFocus
                        onFocus={() => setFocused('phone')} onBlur={() => setFocused(null)}
                        style={{ ...inp(focused === 'phone'), flex: 1 }} />
                    </div>
                    <p style={{ fontSize: '.72rem', color: C.muted, marginTop: '.4rem' }}>We'll send a 6-digit OTP to this WhatsApp number</p>
                  </div>
                  <button type="button" onClick={sendOtp} disabled={otpLoading || phone.length < 10}
                    style={{ width: '100%', padding: '1rem', borderRadius: 11, background: (otpLoading || phone.length < 10) ? C.g400 : '#25D366', color: 'white', border: 'none', fontSize: '.95rem', fontWeight: 600, cursor: (otpLoading || phone.length < 10) ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {otpLoading
                      ? <><span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,.3)', borderTopColor: 'white', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} /> Sending…</>
                      : <><WhatsAppIcon /> Send OTP on WhatsApp</>}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ background: '#F0FFF4', border: `1px solid ${C.g300}`, borderRadius: 10, padding: '.85rem 1rem', fontSize: '.85rem', color: C.g700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <WhatsAppIcon color={C.g600} /> OTP sent to +91 {phone}
                  </div>
                  {devOtp && (
                    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '.85rem 1rem', fontSize: '.85rem', color: '#92400E' }}>
                      WhatsApp not available — your OTP is: <strong style={{ fontSize:'1.1rem', letterSpacing:'.15em' }}>{devOtp}</strong>
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: '.75rem', fontWeight: 600, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', display: 'block', marginBottom: '.4rem' }}>Enter OTP</label>
                    <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0,6))}
                      placeholder="123456" maxLength={6} autoFocus
                      onFocus={() => setFocused('otp')} onBlur={() => setFocused(null)}
                      style={{ ...inp(focused === 'otp'), letterSpacing: '.3em', fontSize: '1.4rem', textAlign: 'center' }} />
                  </div>
                  <button type="button" onClick={verifyOtp} disabled={otpLoading || otp.length < 6}
                    style={{ width: '100%', padding: '1rem', borderRadius: 11, background: (otpLoading || otp.length < 6) ? C.g400 : C.g700, color: 'white', border: 'none', fontSize: '.95rem', fontWeight: 600, cursor: (otpLoading || otp.length < 6) ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {otpLoading
                      ? <><span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,.3)', borderTopColor: 'white', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} /> Verifying…</>
                      : 'Verify & Sign In →'}
                  </button>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button type="button" onClick={() => { setOtpSent(false); setOtp(''); setDevOtp(''); setError('') }}
                      style={{ background: 'none', border: 'none', color: C.g600, fontSize: '.82rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>← Change number</button>
                    {resendTimer > 0
                      ? <span style={{ fontSize: '.78rem', color: C.muted }}>Resend in {resendTimer}s</span>
                      : <button type="button" onClick={sendOtp} style={{ background: 'none', border: 'none', color: C.g600, fontSize: '.82rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontWeight: 600 }}>Resend OTP</button>}
                  </div>
                </>
              )}
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
            <Link to="/" style={{ fontSize: '.85rem', color: C.g600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'color .2s' }}
              onMouseEnter={e => e.currentTarget.style.color = C.g800}
              onMouseLeave={e => e.currentTarget.style.color = C.g600}>
              ← Back to Revive
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        /* Mobile: hide the marketing panel, give the login form the full width */
        @media (max-width: 820px){
          .login-left{ display: none !important; }
          .login-right{ padding: 2.25rem 1.25rem !important; min-height: 100vh; }
        }
      `}</style>
    </div>
  )
}
